/**
 * Auth Profile Duplicate Modal (Phase 9)
 * Shows when duplicate login detected and mode is 'ask'
 * Lets user choose: Update existing, Create new, or Dismiss
 */

import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSync, faPlus, faTimes, faUser, faClock, faExclamationCircle } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import { toast } from 'react-toastify';

const AuthProfileDuplicateModal = ({ duplicate, proxySessionId, duplicateIndex, onClose, onActionComplete, isDarkMode }) => {
  const [processing, setProcessing] = useState(false);

  // Calculate time ago
  const getTimeAgo = (date) => {
    const seconds = Math.floor((new Date() - new Date(date)) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  const handleAction = async (action) => {
    setProcessing(true);

    try {
      const response = await axios.post(
        `/api/proxy-sessions/${proxySessionId}/handle-duplicate`,
        { duplicateIndex, action }
      );

      if (action === 'update') {
        toast.success(`✓ Profile updated: ${response.data.profile.name}`, {
          autoClose: 5000
        });
      } else if (action === 'create_new') {
        toast.success(`✓ New profile created: ${response.data.profile.name}`, {
          autoClose: 5000
        });
      } else {
        toast.info('Duplicate dismissed', { autoClose: 3000 });
      }

      onActionComplete(response.data);
      onClose();
    } catch (err) {
      console.error('Error handling duplicate:', err);
      toast.error(err.response?.data?.error || 'Failed to process duplicate');
      setProcessing(false);
    }
  };

  const existingProfile = duplicate.existingProfileId || {};
  const lastUpdated = existingProfile.sessionRefresh?.lastRefreshedAt || existingProfile.updatedAt;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className={`relative w-full max-w-lg mx-4 rounded-lg shadow-xl ${
        isDarkMode ? 'bg-gray-800 text-gray-100' : 'bg-white text-gray-900'
      }`}>
        {/* Header */}
        <div className={`flex items-center justify-between px-6 py-4 border-b ${
          isDarkMode ? 'border-gray-700' : 'border-gray-200'
        }`}>
          <div className="flex items-center gap-3">
            <FontAwesomeIcon icon={faSync} className="text-blue-500 text-xl" />
            <h2 className="text-lg font-semibold">Login Detected</h2>
          </div>
          <button
            onClick={onClose}
            disabled={processing}
            className={`p-2 rounded hover:bg-opacity-10 hover:bg-gray-500 transition-colors ${
              processing ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          >
            <FontAwesomeIcon icon={faTimes} className="text-gray-400" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-4">
          {/* User info */}
          <div className={`flex items-center gap-2 mb-4 p-3 rounded ${
            isDarkMode ? 'bg-blue-900/20 border border-blue-700/30' : 'bg-blue-50 border border-blue-200'
          }`}>
            <FontAwesomeIcon icon={faUser} className="text-blue-500" />
            <span className="font-medium">User "{duplicate.username}" logged in again</span>
          </div>

          {/* Existing profile info */}
          <div className={`p-4 rounded-lg mb-6 ${
            isDarkMode ? 'bg-gray-700/50' : 'bg-gray-50'
          }`}>
            <div className="flex items-start justify-between mb-2">
              <div>
                <div className="text-sm text-gray-500 mb-1">Existing Profile:</div>
                <div className="font-medium text-base">{duplicate.existingProfileName}</div>
              </div>
              {duplicate.existingProfileUpdateCounter > 0 && (
                <span className={`px-2 py-1 rounded text-xs font-medium ${
                  isDarkMode ? 'bg-purple-900/30 text-purple-300' : 'bg-purple-100 text-purple-700'
                }`}>
                  v{duplicate.existingProfileUpdateCounter + 1}
                </span>
              )}
            </div>

            {lastUpdated && (
              <div className="flex items-center gap-2 text-sm text-gray-500 mt-2">
                <FontAwesomeIcon icon={faClock} className="text-xs" />
                <span>Last updated: {getTimeAgo(lastUpdated)}</span>
              </div>
            )}
          </div>

          {/* Info message */}
          <div className={`flex items-start gap-2 p-3 rounded text-sm mb-6 ${
            isDarkMode ? 'bg-yellow-900/20 text-yellow-200 border border-yellow-700/30' : 'bg-yellow-50 text-yellow-800 border border-yellow-200'
          }`}>
            <FontAwesomeIcon icon={faExclamationCircle} className="mt-0.5 flex-shrink-0" />
            <div>
              <strong>What would you like to do?</strong>
              <div className="mt-1 text-xs opacity-90">
                Updating will refresh the credentials and increment the version.
                Creating new will keep both profiles.
              </div>
            </div>
          </div>

          {/* Action buttons */}
          <div className="space-y-2">
            {/* Update Existing */}
            <button
              onClick={() => handleAction('update')}
              disabled={processing}
              className={`w-full flex items-center justify-center gap-3 px-4 py-3 rounded-lg font-medium transition-all ${
                isDarkMode
                  ? 'bg-blue-600 hover:bg-blue-700 text-white disabled:bg-gray-700'
                  : 'bg-blue-500 hover:bg-blue-600 text-white disabled:bg-gray-300'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              <FontAwesomeIcon icon={faSync} className={processing ? 'animate-spin' : ''} />
              <span>Update Existing Profile</span>
              {duplicate.existingProfileUpdateCounter >= 0 && (
                <span className="opacity-75 text-sm">
                  (v{duplicate.existingProfileUpdateCounter + 1} → v{duplicate.existingProfileUpdateCounter + 2})
                </span>
              )}
            </button>

            {/* Create New */}
            <button
              onClick={() => handleAction('create_new')}
              disabled={processing}
              className={`w-full flex items-center justify-center gap-3 px-4 py-3 rounded-lg font-medium transition-all ${
                isDarkMode
                  ? 'bg-green-600 hover:bg-green-700 text-white disabled:bg-gray-700'
                  : 'bg-green-500 hover:bg-green-600 text-white disabled:bg-gray-300'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              <FontAwesomeIcon icon={faPlus} />
              <span>Create New Profile</span>
            </button>

            {/* Dismiss */}
            <button
              onClick={() => handleAction('dismiss')}
              disabled={processing}
              className={`w-full flex items-center justify-center gap-3 px-4 py-3 rounded-lg font-medium transition-all ${
                isDarkMode
                  ? 'bg-gray-700 hover:bg-gray-600 text-gray-300 disabled:bg-gray-800'
                  : 'bg-gray-200 hover:bg-gray-300 text-gray-700 disabled:bg-gray-100'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              <FontAwesomeIcon icon={faTimes} />
              <span>Dismiss</span>
            </button>
          </div>
        </div>

        {/* Footer hint */}
        <div className={`px-6 py-3 text-xs text-center border-t ${
          isDarkMode ? 'text-gray-500 border-gray-700' : 'text-gray-400 border-gray-200'
        }`}>
          You can change this behavior in Web Target settings
        </div>
      </div>
    </div>
  );
};

export default AuthProfileDuplicateModal;
